import './fragments/contentfulProduct'

import { ALBY_BRAND_ID } from '@ecomm/ai-chat/constants'
import { listenToJebbitPostMessages } from '@ecomm/cdp-tracking'
import { EnvProvider } from '@ecomm/data-env-variables/EnvProvider'
// Below import is from an autogenerated file (gitignored), generated at the time of
// pre-building the site. It contains commercetools products data. If
// you don't see this file you need to run the build/develop command first or node tools/scripts/fetchProducts.mjs.
import { commerceToolProducts } from '@ecomm/data-products/commerceToolProducts'
import { cookieAtom } from '@ecomm/data-storage'
import { ErrorBoundary } from '@ecomm/error-handling'
import { parseProducts } from '@ecomm/gatsby-plugins-utils'
import { InjectContext } from '@ecomm/gatsby-wrappers'
import { optimizelyClientAtom } from '@ecomm/optimizely-utils'
// Below import is from an autogenerated file (gitignored), generated at the time of
// pre-building the site. It contains Ninetailed experience data. If
// you don't see this file you need to run the build/develop command first or node tools/scripts/fetch-static-data.mjs.
import { ninetailedExperienceData } from '@ecomm/shared-ninetailed'
import {
  ExperiencesContext,
  createExperiencesDictionary
} from '@ecomm/shared-ninetailed/ExperiencesContext'
import { ninetailedFeatureFlagsAtom } from '@ecomm/shared-ninetailed/ninetailedFeatureFlagsAtom'
import { useNinetailedFeatureFlagAtomisedData } from '@ecomm/shared-ninetailed/useNinetailedFeatureFlagAtomisedData'
import { initBraze } from '@ecomm/tracking/braze'
import { OptimizelyProvider } from '@ecomm/tracking/optimizely/OptimizelyProvider'
import { useTrackingVisitorArrival } from '@ecomm/tracking/useTrackingVisitorArrival'
import { getLocale } from '@ecomm/utils/locale/getLocale'
import type { Locale } from '@simplisafe/ewok'
import { createStore } from '@simplisafe/ss-ecomm-data/redux/store'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as O from 'fp-ts/lib/Option'
import { Script, ScriptStrategy } from 'gatsby'
import { useHydrateAtoms } from 'jotai/utils'
import { type ReactElement, Suspense, useEffect, useMemo } from 'react'
import { CookiesProvider } from 'react-cookie'
import { Helmet } from 'react-helmet'
import { Provider } from 'react-redux'
import { DevTools } from './DevTools'
import { HideOneTrustBanner } from './HideOneTrustBanner'

type WrapWithProviderProps = {
  readonly element: ReactElement
}

const PX_ID = process.env['GATSBY_PX_APP_ID']
const affirmKey = process.env['GATSBY_AFFIRM_PUBLIC_KEY']
const locale: Locale = getLocale()
const oneTrustDomainScript = process.env['ONETRUST_DOMAIN_SCRIPT']
const rudderStackWriteKey = process.env['GATSBY_RUDDERSTACK_WRITE_KEY']
const rudderStackDataPlaneUrl = process.env['GATSBY_RUDDERSTACK_DATAPLANE_URL']
const enableAIChatEnv = process.env['GATSBY_ENABLE_AI_CHAT']

function Wrapper<T extends WrapWithProviderProps>({ element }: T) {
  // Instantiating store in `wrapRootElement` handler ensures:
  //  - there is fresh store for each SSR page
  //  - it will be called only once in browser, when React mounts
  // Wrapped in useMemo because useStaticQuery (below) can cause rerenders in dev:
  //   see: https://github.com/gatsbyjs/gatsby/issues/29011
  const store = useMemo(() => {
    return createStore()
  }, [])

  const ninetailedFeatureFlagAtomisedData =
    useNinetailedFeatureFlagAtomisedData()

  const products = parseProducts(commerceToolProducts)

  // Track the initial session visit to Rudderstack. Should only fire
  // once on application lifecycle (at startup).
  useTrackingVisitorArrival()

  //Rudderstack event tracking - Jebbit Post Messages 'quote_wizard_start' and 'quote_wizard_complete'
  useEffect(() => {
    listenToJebbitPostMessages()
  }, [])

  useHydrateAtoms([
    [optimizelyClientAtom, O.none],
    [cookieAtom, globalThis.document?.cookie],
    [ninetailedFeatureFlagsAtom, ninetailedFeatureFlagAtomisedData]
  ])

  const queryClient = new QueryClient()

  const enableAIChat = Boolean(
    enableAIChatEnv === 'true' && locale === 'en-US' && ALBY_BRAND_ID
  )

  return (
    <ErrorBoundary>
      <EnvProvider config={{ locale }}>
        {locale === 'en-US' ? <HideOneTrustBanner /> : null}
        <Helmet>
          {/* Loads the base RudderStack SDK, which can latter be used to load the frontend source configuration and dependent SDKs (dependent on consent). */}
          <link
            href={`${process.env['GATSBY_RUDDERSTACK_DATAPLANE_URL']}`}
            rel="preconnect"
          />
          {locale === 'en-US' ? (
            <link href="experience.ninetailed.co" rel="dns-prefetch" />
          ) : null}
          <link
            as="script"
            href={`${process.env['GATSBY_RUDDERSTACK_DATAPLANE_URL']}/v3/modern/rsa.min.js`}
            rel="preload"
          />
          <script id="rudder-stack-sdk" type="text/javascript">
            {`!function(){"use strict";window.RudderSnippetVersion="3.0.3";var sdkBaseUrl="${process.env['GATSBY_RUDDERSTACK_DATAPLANE_URL']}/v3"
  ;var sdkName="rsa.min.js";var asyncScript=true;window.rudderAnalyticsBuildType="legacy",window.rudderanalytics=[]
  ;var e=["setDefaultInstanceKey","load","ready","page","track","identify","alias","group","reset","setAnonymousId","startSession","endSession","consent"]
  ;for(var n=0;n<e.length;n++){var t=e[n];window.rudderanalytics[t]=function(e){return function(){
  window.rudderanalytics.push([e].concat(Array.prototype.slice.call(arguments)))}}(t)}try{
  new Function('return import("")'),window.rudderAnalyticsBuildType="modern"}catch(a){}
  if(window.rudderAnalyticsMount=function(){
  "undefined"==typeof globalThis&&(Object.defineProperty(Object.prototype,"__globalThis_magic__",{get:function get(){
  return this},configurable:true}),__globalThis_magic__.globalThis=__globalThis_magic__,
  delete Object.prototype.__globalThis_magic__);var e=globalThis.document.createElement("script")
  ;e.src="".concat(sdkBaseUrl,"/").concat(window.rudderAnalyticsBuildType,"/").concat(sdkName),e.async=asyncScript,
  globalThis.document.head?globalThis.document.head.appendChild(e):globalThis.document.body.appendChild(e)
  },"undefined"==typeof Promise||"undefined"==typeof globalThis){var d=globalThis.document.createElement("script")
  ;d.src="https://polyfill-fastly.io/v3/polyfill.min.js?version=3.111.0&features=Symbol%2CPromise&callback=rudderAnalyticsMount",
  d.async=asyncScript,globalThis.document.head?globalThis.document.head.appendChild(d):globalThis.document.body.appendChild(d)}else{
  window.rudderAnalyticsMount()}}();`}
          </script>
          {oneTrustDomainScript ? (
            <script
              data-domain-script={oneTrustDomainScript}
              src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
              type="text/javascript"
            ></script>
          ) : null}
          {locale === 'en-US' && oneTrustDomainScript ? (
            <script
              id="optanon-configurator"
              rs-data-plane={rudderStackDataPlaneUrl}
              rs-write-key={rudderStackWriteKey}
              src="/ot-rudder-us-20241112.js"
              type="text/javascript"
            />
          ) : null}
          {enableAIChat ? (
            <script
              async
              type="text/javascript"
              src={`https://cdn.alby.com/apps/launcher/embed.js?brandId=${ALBY_BRAND_ID}`}
            />
          ) : null}
          {locale === 'en-GB' && oneTrustDomainScript ? (
            <script
              id="optanon-configurator"
              rs-data-plane={rudderStackDataPlaneUrl}
              rs-write-key={rudderStackWriteKey}
              src="/ot-rudder-uk-20241112.js"
              type="text/javascript"
            />
          ) : null}
        </Helmet>
        <OptimizelyProvider datafile={{}}>
          <DevTools />
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <InjectContext products={products}>
                <CookiesProvider>
                  <ExperiencesContext.Provider
                    value={createExperiencesDictionary(
                      ninetailedExperienceData
                    )}
                  >
                    {element}
                  </ExperiencesContext.Provider>
                </CookiesProvider>
              </InjectContext>
            </QueryClientProvider>
          </Provider>
        </OptimizelyProvider>
        <Script src={`/old-browsers.js`} strategy={ScriptStrategy.idle} />
        <Script id={PX_ID} src={`/perimeterx.js`} />
        {affirmKey ? (
          <Script id={affirmKey} src={`/affirm.js`} strategy="idle" />
        ) : null}
        <Script
          onLoad={() => {
            initBraze()
          }}
          src={`/braze-web-sdk-20230207.js`}
          strategy="idle"
        />
        <Script
          src="https://d2jjzw81hqbuqv.cloudfront.net/integration/clients/simplisafe-staging.min.js"
          strategy="idle"
          type="text/javascript"
        />
        {enableAIChat ? (
          <Script
            src="/ai-chat-trackevents.js"
            strategy="idle"
            type="text/javascript"
          />
        ) : null}
        <Script
          id="lunioScript"
          src="https://client.prod.mplat-ppcprotect.com/MI8U3hXMDTqxucka-wtGKd94hnuDZvlp1LeDG958.js"
          strategy="idle"
          type="text/javascript"
        />
      </EnvProvider>
    </ErrorBoundary>
  )
}

// Wrap the wrapper in another wrapper to allow the use of React hooks
// https://github.com/gatsbyjs/gatsby/issues/22833#issuecomment-609370401
export function WrapRootElement<T extends WrapWithProviderProps>(t: T) {
  return (
    <ErrorBoundary>
      <Suspense fallback={null}>
        <Wrapper {...t} />
      </Suspense>
    </ErrorBoundary>
  )
}
